/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  Alert,
  AlertTitle,
  Container,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BackNavigationAppBar,
  ContextMenuComplete,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { Conversation, ConversationType } from '@marageti/z4-sdk/lib/travel';
import ZicassoChat from '../../../../components/zicasso-chat';
import ConversationParticipantNames from '../../../../components/conversation-participant-names';
import RightSidebar from './right-sidebar';

const getTitle = (conversationType: ConversationType, travelerName: string) => {
  if (conversationType === ConversationType.TravelerToPartner) {
    return travelerName;
  }
  return `Zicasso | ${travelerName}`;
};

export const ConversationContainer = () => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { personId, conversationId } = useParams<{ personId: string; conversationId?: string }>();
  const apiClient = useApiClient();

  const { data: conversation, error, isLoading: conversationLoading } = useFetch<Conversation>(
    apiClient.conversationsClient.getConversation,
    [conversationId],
    !!conversationId,
  );

  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);

  const toggleRightSidebar = () => {
    setIsRightSidebarOpen((prev: boolean) => !prev);
  };

  const navigateToTripMatch = () => {
    if (conversation?.tripMatchId) {
      navigate(`/trip-matches/${conversation.tripMatchId}`);
    }
  };

  const onClickBack = () => {
    const path = personId ? `/inbox/${personId}` : '/inbox';
    const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
    navigate(`${path}${search}`);
  };

  const title = getTitle(
    conversation?.conversationType ?? ConversationType.TravelerToPartner,
    conversation?.primaryTravelerName || 'Traveler',
  );

  if (error) {
    return (
      <Container sx={{ my: 3 }}>
        <Alert severity="error">
          <AlertTitle>
            {isMobile ? 'Error' : 'There was an error loading this conversation'}
          </AlertTitle>
          {error?.code && `Code: ${error.code} `}
          {error?.message || 'Please contact support'}
          <br />
          {error?.id && `ID: ${error.id}`}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Mobile App Bar */}
      {isMobile && (
      <BackNavigationAppBar
        primary={conversationLoading ? <Skeleton width={75} /> : (title || <Skeleton width={75} />)}
        endAdornment={
          conversation?.tripMatchId ? (
            <ContextMenuComplete
              id="trip-match-messageing-context-menu"
              items={[{ label: 'View Trip Details', action: navigateToTripMatch }]}
            />
          ) : undefined
        }
        onClick={onClickBack}
      />
      )}

      {/* Main Container */}
      <Box
        sx={{
          ...(isMobile
            ? {
              position: 'fixed',
              top: '72px',
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'primary.contrastText',
              zIndex: 1200,
              px: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }
            : {
              display: 'flex',
              flexDirection: 'row',
              height: '100%',
              position: 'relative',
              minWidth: 0,
              overflow: 'hidden',
              pt: 0,
            }),
        }}
      >
        {/* Chat Section */}
        <Box
          sx={{
            ...(isMobile
              ? {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }
              : {
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0',
                height: '100%',
                minWidth: '250px',
                overflow: 'hidden',
                pt: 2,
                pl: 6,
              }),
          }}
        >
          {/* Desktop Header */}
          {!isMobile && (
          <Box
            sx={{
              mb: 2,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              minWidth: 0,
            }}
          >
            <Stack spacing="2px" sx={{ minWidth: 0, flex: '1 1 0' }}>
              <Typography
                variant="h3"
                noWrap
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {conversationLoading ? <Skeleton width={120} /> : title}
              </Typography>
              <ConversationParticipantNames
                conversationId={conversation?.id}
                conversationType={conversation?.conversationType!}
                typographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </Stack>
            <Button
              onClick={toggleRightSidebar}
              sx={{
                flexShrink: 0,
                py: '4px',
                px: '8px',
                fontSize: '13px',
                fontWeight: 700,
                lineHeight: '20px',
                borderColor: 'divider',
              }}
              variant="outlined"
            >
              {!isRightSidebarOpen ? 'Show Details' : 'Hide Details'}
            </Button>
          </Box>
          )}

          {/* Chat Container */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: '250px',
              overflow: 'hidden',
            }}
          >
            <ZicassoChat />
          </Box>
        </Box>

        {/* Desktop Right Sidebar */}
        {!isMobile && (
        <RightSidebar
          isRightSidebarOpen={isRightSidebarOpen}
          conversation={conversation ?? undefined}
        />
        )}
      </Box>
    </>
  );
};

export default ConversationContainer;
